import { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import { adminApi, type AdminPlayerOption } from "../api/admin";
import type { RouletteAdminConfig, RouletteSegmentConfig } from "../api/roulette";

type GuildBattleAdminStatus = Awaited<ReturnType<typeof adminApi.guildBattleStatus>>;
type PartyArenaAdminStatus = Awaited<ReturnType<typeof adminApi.partyArenaStatus>>;

export function AdminScreen() {
  const { setScreen, refreshDailyReward, dailyRewardStatus, adminShowAssetPaths, setAdminShowAssetPaths, adminCampaignBalanceMode, setAdminCampaignBalanceMode } =
    useGameStore();
  const [loading, setLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [guildStatus, setGuildStatus] = useState<GuildBattleAdminStatus | null>(null);
  const [guildStatusLoading, setGuildStatusLoading] = useState(false);
  const [partyStatus, setPartyStatus] = useState<PartyArenaAdminStatus | null>(null);
  const [partyStatusLoading, setPartyStatusLoading] = useState(false);
  const [soloPlayerQuery, setSoloPlayerQuery] = useState("");
  const [soloPlayerResults, setSoloPlayerResults] = useState<AdminPlayerOption[]>([]);
  const [soloSelectedPlayer, setSoloSelectedPlayer] = useState<AdminPlayerOption | null>(null);
  const [rouletteConfig, setRouletteConfig] = useState<RouletteAdminConfig | null>(null);
  const [rouletteConfigLoading, setRouletteConfigLoading] = useState(false);

  const refreshGuildStatus = useCallback(async () => {
    setGuildStatusLoading(true);
    try {
      const res = await adminApi.guildBattleStatus();
      setGuildStatus(res);
    } catch {
      setGuildStatus(null);
    } finally {
      setGuildStatusLoading(false);
    }
  }, []);

  const refreshPartyStatus = useCallback(async () => {
    setPartyStatusLoading(true);
    try {
      const res = await adminApi.partyArenaStatus();
      setPartyStatus(res);
    } catch {
      setPartyStatus(null);
    } finally {
      setPartyStatusLoading(false);
    }
  }, []);

  const refreshRouletteConfig = useCallback(async () => {
    setRouletteConfigLoading(true);
    try {
      const res = await adminApi.getRouletteConfig();
      setRouletteConfig(res.config);
    } catch {
      setRouletteConfig(null);
    } finally {
      setRouletteConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshGuildStatus();
    void refreshPartyStatus();
    void refreshRouletteConfig();
  }, [refreshGuildStatus, refreshPartyStatus, refreshRouletteConfig]);

  useEffect(() => {
    const q = soloPlayerQuery.trim();
    if (q.length < 2) {
      setSoloPlayerResults([]);
      return;
    }
    const t = setTimeout(() => {
      void adminApi
        .searchPlayers(q)
        .then((res) => setSoloPlayerResults(res.players))
        .catch(() => setSoloPlayerResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [soloPlayerQuery]);

  const runAction = async (key: string, fn: () => Promise<{ message: string }>) => {
    setLoading(key);
    setMessage(null);
    try {
      const res = await fn();
      await refreshDailyReward();
      setMessage(res.message);
      await refreshGuildStatus();
      await refreshPartyStatus();
      await refreshRouletteConfig();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Action failed");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="p-4 space-y-4 pb-8">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScreen("main")}
          className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
        >
          ← Back
        </button>
        <h1 className="text-xl font-bold font-game text-gold-400">👑 Admin</h1>
      </div>

      <p className="text-gray-400 text-sm">
        Internal tools for testing game systems. Visible only to admin accounts.
      </p>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={adminShowAssetPaths}
            onChange={(e) => setAdminShowAssetPaths(e.target.checked)}
            className="mt-1 rounded border-kingdom-mid"
          />
          <div>
            <div className="font-bold text-white text-sm">Show asset paths</div>
            <p className="text-xs text-gray-400 mt-0.5">
              Display icon file paths under items, consumables, hero portrait, and skills (small monospace text).
            </p>
          </div>
        </label>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={adminCampaignBalanceMode}
            onChange={(e) => setAdminCampaignBalanceMode(e.target.checked)}
            className="mt-1 rounded border-kingdom-mid"
          />
          <div>
            <div className="font-bold text-white text-sm">Campaign battle balance mode</div>
            <p className="text-xs text-gray-400 mt-0.5">
              After solo campaign fights, tune this node’s encounter and stats. Duplicate adds a new row in
              monster_templates (global catalog). Apply reopens the node on the map without rolling back rewards.
            </p>
          </div>
        </label>
      </section>

      <div className="grid gap-3">
        <AdminCard
          icon="🎁"
          title="Grants"
          subtitle="Spawn items, gems, luck stones, gold, or diamonds for any player"
          onClick={() => setScreen("adminGrants")}
        />
        <AdminCard
          icon="🦸"
          title="Character (Self)"
          subtitle="Grant gold, diamonds, and prep XP for level-up"
          onClick={() => setScreen("adminCharacter")}
        />
        <AdminCard
          icon="🐛"
          title="Debug Console"
          subtitle="Telegram params, API health, battle log"
          onClick={() => setScreen("debug")}
        />
      </div>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">📅 Daily Rewards Testing</h2>
          <p className="text-xs text-gray-400 mt-1">
            Advance the calendar, then return to Home to claim the next day reward.
          </p>
        </div>

        {dailyRewardStatus && (
          <div className="bg-kingdom-dark rounded-lg p-3 text-xs text-gray-300 space-y-1">
            <div>
              Streak day: <span className="text-gold-300 font-bold">{dailyRewardStatus.streak_day}</span> / {dailyRewardStatus.max_day}
            </div>
            <div>
              Can claim: <span className={dailyRewardStatus.can_claim ? "text-emerald-400" : "text-gray-500"}>
                {dailyRewardStatus.can_claim ? "yes" : "no"}
              </span>
            </div>
            {dailyRewardStatus.streak_broken && (
              <div className="text-amber-300">Streak was reset due to missed days.</div>
            )}
          </div>
        )}

        <div className="grid gap-2">
          <AdminActionButton
            label="⏭ Advance day"
            loading={loading === "advance"}
            onClick={() => runAction("advance", () => adminApi.advanceDailyDay())}
          />
          <AdminActionButton
            label="🔄 Reset cycle to day 1"
            loading={loading === "reset"}
            onClick={() => runAction("reset", () => adminApi.resetDailyCycle())}
          />
          <AdminActionButton
            label="⚠️ Simulate missed day"
            loading={loading === "missed"}
            onClick={() => runAction("missed", () => adminApi.simulateMissedDailyDay())}
          />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">🐲 World Boss Testing</h2>
          <p className="text-xs text-gray-400 mt-1">
            Force-open the boss window for testing, then respawn with fresh HP and an empty damage
            leaderboard. Does not affect the Amethyst Mine event.
          </p>
        </div>
        <div className="grid gap-2">
          <AdminActionButton
            label="▶️ Force open World Boss window (120 min)"
            loading={loading === "world-boss-open"}
            onClick={() => runAction("world-boss-open", () => adminApi.openWorldBossWindow(120))}
          />
          <AdminActionButton
            label="⏹ Close World Boss window override"
            loading={loading === "world-boss-close"}
            onClick={() => runAction("world-boss-close", () => adminApi.closeWorldBossWindow())}
          />
          <AdminActionButton
            label="♻️ Respawn World Boss"
            loading={loading === "world-boss-respawn"}
            onClick={() => runAction("world-boss-respawn", () => adminApi.respawnWorldBoss())}
          />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">🟣 Amethyst Mine Testing</h2>
          <p className="text-xs text-gray-400 mt-1">
            Force the daily mine event (higher ore caps) or clear Redis slot state. Separate from
            World Boss.
          </p>
        </div>
        <div className="grid gap-2">
          <AdminActionButton
            label="▶️ Force mine event (120 min)"
            loading={loading === "mine-event-open"}
            onClick={() => runAction("mine-event-open", () => adminApi.openMineEvent(120))}
          />
          <AdminActionButton
            label="⏹ Close mine event override"
            loading={loading === "mine-event-close"}
            onClick={() => runAction("mine-event-close", () => adminApi.closeMineEvent())}
          />
          <AdminActionButton
            label="♻️ Reset mine slots (Redis)"
            loading={loading === "mine-slots-reset"}
            onClick={() => runAction("mine-slots-reset", () => adminApi.resetMineSlots())}
          />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">🎡 Fortune Wheel</h2>
          <p className="text-xs text-gray-400 mt-1">
            Activate the roulette on Main screen, configure 12 weighted segments and spin cost.
          </p>
        </div>

        {rouletteConfigLoading && !rouletteConfig ? (
          <p className="text-xs text-gray-500">Loading roulette config…</p>
        ) : rouletteConfig ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-300">
              Status:{" "}
              <span className={rouletteConfig.active ? "text-emerald-400" : "text-gray-500"}>
                {rouletteConfig.active ? "ACTIVE" : "inactive"}
              </span>
              {rouletteConfig.active_until ? (
                <span className="text-gray-500"> · until {rouletteConfig.active_until}</span>
              ) : null}
            </p>

            <div className="grid gap-2 sm:grid-cols-3">
              <label className="text-xs text-gray-400 space-y-1">
                Spin cost type
                <select
                  value={rouletteConfig.spin_cost.type}
                  onChange={(e) =>
                    setRouletteConfig({
                      ...rouletteConfig,
                      spin_cost: {
                        ...rouletteConfig.spin_cost,
                        type: e.target.value as RouletteAdminConfig["spin_cost"]["type"],
                      },
                    })
                  }
                  className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-sm"
                >
                  <option value="consumable">consumable</option>
                  <option value="gold">gold</option>
                  <option value="diamonds">diamonds</option>
                </select>
              </label>
              <label className="text-xs text-gray-400 space-y-1">
                Item id (consumable)
                <input
                  value={rouletteConfig.spin_cost.item_id ?? ""}
                  disabled={rouletteConfig.spin_cost.type !== "consumable"}
                  onChange={(e) =>
                    setRouletteConfig({
                      ...rouletteConfig,
                      spin_cost: { ...rouletteConfig.spin_cost, item_id: e.target.value || null },
                    })
                  }
                  className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-sm disabled:opacity-40"
                />
              </label>
              <label className="text-xs text-gray-400 space-y-1">
                Cost amount
                <input
                  type="number"
                  min={1}
                  value={rouletteConfig.spin_cost.amount}
                  onChange={(e) =>
                    setRouletteConfig({
                      ...rouletteConfig,
                      spin_cost: {
                        ...rouletteConfig.spin_cost,
                        amount: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                      },
                    })
                  }
                  className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-2 py-1.5 text-white text-sm"
                />
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="text-gray-500 border-b border-kingdom-mid">
                    <th className="py-1 pr-2">Slot</th>
                    <th className="py-1 pr-2">Reward</th>
                    <th className="py-1 pr-2">Item / amount</th>
                    <th className="py-1 pr-2">Qty</th>
                    <th className="py-1">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {rouletteConfig.segments.map((seg, idx) => (
                    <RouletteSegmentRow
                      key={seg.slot}
                      segment={seg}
                      onChange={(next) => {
                        const segments = [...rouletteConfig.segments];
                        segments[idx] = next;
                        setRouletteConfig({ ...rouletteConfig, segments });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <AdminActionButton
              label="💾 Save roulette config"
              loading={loading === "roulette-save"}
              onClick={() =>
                runAction("roulette-save", async () => {
                  const res = await adminApi.saveRouletteConfig(rouletteConfig);
                  return { message: res.message };
                })
              }
            />
          </div>
        ) : (
          <p className="text-xs text-rose-400">Failed to load roulette config.</p>
        )}

        <div className="grid gap-2">
          <AdminActionButton
            label="▶️ Activate Fortune Wheel (120 min)"
            loading={loading === "roulette-activate"}
            onClick={() =>
              runAction("roulette-activate", async () => {
                const res = await adminApi.activateRoulette(120);
                return { message: res.message };
              })
            }
          />
          <AdminActionButton
            label="⏹ Deactivate Fortune Wheel"
            loading={loading === "roulette-deactivate"}
            onClick={() =>
              runAction("roulette-deactivate", async () => {
                const res = await adminApi.deactivateRoulette();
                return { message: res.message };
              })
            }
          />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">⚔️ Guild Battle Testing</h2>
          <p className="text-xs text-gray-400 mt-1">
            Reset week, force start, or advance phases for testing. Change schedule in game config (guild.balance).
          </p>
        </div>
        <div className="grid gap-2">
          <div className="rounded-lg border border-kingdom-mid bg-kingdom-dark/50 p-3 space-y-1 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-white">Current week status</span>
              <button
                type="button"
                disabled={guildStatusLoading}
                onClick={() => void refreshGuildStatus()}
                className="text-[10px] text-gray-400 hover:text-white disabled:opacity-40"
              >
                {guildStatusLoading ? "…" : "Refresh"}
              </button>
            </div>
            {guildStatus?.active ? (
              <>
                <p className="text-gray-300">
                  Week <span className="text-white font-mono">{guildStatus.week_key}</span> · phase{" "}
                  <span className="text-amber-200 capitalize">{guildStatus.phase}</span> ·{" "}
                  <span className="text-violet-200">{guildStatus.status}</span>
                </p>
                <p className="text-gray-500">
                  Queue: {guildStatus.match_queue_len ?? 0} · current match:{" "}
                  {guildStatus.has_current_match ? "yes" : "no"}
                </p>
                {guildStatus.status === "awaiting_replay" && (
                  <p className="text-rose-300">
                    Stuck on replay ack — use &quot;Skip stuck replay&quot; below or reset the week.
                  </p>
                )}
                {guildStatus.message && <p className="text-gray-500">{guildStatus.message}</p>}
              </>
            ) : (
              <p className="text-gray-500">No active guild battle week.</p>
            )}
          </div>
          <AdminActionButton
            label="♻️ Reset guild battle week"
            loading={loading === "guild-reset"}
            onClick={() => runAction("guild-reset", () => adminApi.resetGuildBattle(false))}
          />
          <AdminActionButton
            label="♻️ Reset week + palaces"
            loading={loading === "guild-reset-palaces"}
            onClick={() => runAction("guild-reset-palaces", () => adminApi.resetGuildBattle(true))}
          />
          <AdminActionButton
            label="▶️ Force start battle week"
            loading={loading === "guild-start"}
            onClick={() => runAction("guild-start", () => adminApi.startGuildBattle())}
          />
          <AdminActionButton
            label="⏭ Skip watch / advance to Platinum"
            loading={loading === "guild-advance"}
            onClick={() => runAction("guild-advance", () => adminApi.advanceGuildBattle())}
          />
          <AdminActionButton
            label="⏱ Extend check-in (+5 min)"
            loading={loading === "guild-extend"}
            onClick={() => runAction("guild-extend", () => adminApi.extendGuildCheckin(5))}
          />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">🏟 Solo Arena Ladder</h2>
          <p className="text-xs text-gray-400 mt-1">
            Reset solo daily fight attempts for everyone on the ladder (UTC day). Remove a player from the top-30 ladder (slot becomes an NPC guard). Does not reset battles or insignia.
          </p>
        </div>
        <AdminActionButton
          label="Reset daily solo attempts for all players (UTC)"
          loading={loading === "solo-reset-attempts"}
          onClick={() =>
            runAction("solo-reset-attempts", () => adminApi.resetSoloArenaDailyAttempts())
          }
        />
        <div className="border-t border-kingdom-mid pt-3 space-y-3">
          <p className="text-xs text-gray-500">Remove one player from the ladder</p>
        <input
          type="search"
          value={soloPlayerQuery}
          onChange={(e) => {
            setSoloPlayerQuery(e.target.value);
            setSoloSelectedPlayer(null);
          }}
          placeholder="Search player by name or id…"
          className="w-full px-3 py-2 rounded-lg bg-kingdom-dark border border-kingdom-mid text-sm text-white"
        />
        {soloPlayerResults.length > 0 && (
          <ul className="max-h-32 overflow-y-auto space-y-1 text-xs">
            {soloPlayerResults.map((p) => (
              <li key={p.user_id}>
                <button
                  type="button"
                  onClick={() => {
                    setSoloSelectedPlayer(p);
                    setSoloPlayerQuery(p.label);
                    setSoloPlayerResults([]);
                  }}
                  className={clsx(
                    "w-full text-left px-2 py-1.5 rounded border",
                    soloSelectedPlayer?.user_id === p.user_id
                      ? "border-amber-500 text-amber-200"
                      : "border-kingdom-mid text-gray-300",
                  )}
                >
                  {p.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        <AdminActionButton
          label={
            soloSelectedPlayer
              ? `Remove ${soloSelectedPlayer.hero_name ?? soloSelectedPlayer.label} from ladder`
              : "Remove selected player from ladder"
          }
          loading={loading === "solo-remove"}
          disabled={!soloSelectedPlayer}
          onClick={() =>
            soloSelectedPlayer &&
            runAction("solo-remove", () => adminApi.removeSoloArenaPlayer(soloSelectedPlayer.user_id))
          }
        />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">⚔️ Party Arena Testing</h2>
          <p className="text-xs text-gray-400 mt-1">
            Force-open party arena outside the UTC schedule for testing matchmaking and battles.
          </p>
        </div>
        <div className="grid gap-2">
          <div className="rounded-lg border border-kingdom-mid bg-kingdom-dark/50 p-3 space-y-1 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-white">Party arena status</span>
              <button
                type="button"
                disabled={partyStatusLoading}
                onClick={() => void refreshPartyStatus()}
                className="text-[10px] text-gray-400 hover:text-white disabled:opacity-40"
              >
                {partyStatusLoading ? "…" : "Refresh"}
              </button>
            </div>
            {partyStatus ? (
              <>
                <p className="text-gray-300">
                  Open:{" "}
                  <span className={partyStatus.active ? "text-emerald-300" : "text-rose-300"}>
                    {partyStatus.active ? "yes" : "no"}
                  </span>
                  {partyStatus.schedule_open != null && (
                    <>
                      {" "}
                      · schedule:{" "}
                      <span className="text-violet-200">{partyStatus.schedule_open ? "open" : "closed"}</span>
                    </>
                  )}
                </p>
                {partyStatus.admin_override && (
                  <p className="text-violet-300">
                    Admin override active
                    {partyStatus.admin_override_end && (
                      <> until {new Date(partyStatus.admin_override_end).toLocaleString()}</>
                    )}
                  </p>
                )}
                <p className="text-gray-500">
                  Queue: {partyStatus.matchmaking_queue_len ?? 0} · waiting rooms:{" "}
                  {partyStatus.waiting_rooms_count ?? 0}
                </p>
              </>
            ) : (
              <p className="text-gray-500">Could not load party arena status.</p>
            )}
          </div>
          <AdminActionButton
            label="▶️ Force open party arena (120 min)"
            loading={loading === "party-open"}
            onClick={() => runAction("party-open", () => adminApi.openPartyArena(120))}
          />
          <AdminActionButton
            label="⏹ Close admin override"
            loading={loading === "party-close"}
            onClick={() => runAction("party-close", () => adminApi.closePartyArena())}
          />
        </div>
      </section>

      {message && (
        <div className="text-xs text-emerald-300 bg-emerald-950/40 border border-emerald-800 rounded-lg p-3">
          {message}
        </div>
      )}
    </div>
  );
}

function AdminCard({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
  hint,
}: {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 hover:border-kingdom-glow transition-all disabled:opacity-60"
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl">{icon}</span>
        <div>
          <div className="font-bold text-white">{title}</div>
          <div className="text-xs text-gray-400">{subtitle}</div>
          {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
        </div>
      </div>
    </button>
  );
}

function RouletteSegmentRow({
  segment,
  onChange,
}: {
  segment: RouletteSegmentConfig;
  onChange: (next: RouletteSegmentConfig) => void;
}) {
  return (
    <tr className="border-b border-kingdom-mid/50">
      <td className="py-1.5 pr-2 text-gray-400">{segment.slot}</td>
      <td className="py-1.5 pr-2">
        <select
          value={segment.reward.type}
          onChange={(e) =>
            onChange({
              ...segment,
              reward: {
                ...segment.reward,
                type: e.target.value as RouletteSegmentConfig["reward"]["type"],
              },
            })
          }
          className="w-full rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white"
        >
          <option value="gold">gold</option>
          <option value="diamonds">diamonds</option>
          <option value="consumable">consumable</option>
          <option value="mount_stones">mount_stones</option>
          <option value="mount_card">mount_card</option>
        </select>
      </td>
      <td className="py-1.5 pr-2">
        {segment.reward.type === "consumable" ? (
          <input
            value={segment.reward.item_id ?? ""}
            onChange={(e) =>
              onChange({
                ...segment,
                reward: { ...segment.reward, item_id: e.target.value || null },
              })
            }
            className="w-full rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white"
          />
        ) : segment.reward.type === "mount_card" ? (
          <input
            value={segment.reward.mount_code ?? ""}
            onChange={(e) =>
              onChange({
                ...segment,
                reward: { ...segment.reward, mount_code: e.target.value || null },
              })
            }
            className="w-full rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white"
          />
        ) : (
          <input
            type="number"
            min={1}
            value={
              segment.reward.type === "mount_stones"
                ? (segment.reward.quantity ?? 1)
                : (segment.reward.amount ?? 1)
            }
            onChange={(e) =>
              onChange({
                ...segment,
                reward: {
                  ...segment.reward,
                  ...(segment.reward.type === "mount_stones"
                    ? { quantity: Math.max(1, Number.parseInt(e.target.value, 10) || 1) }
                    : { amount: Math.max(1, Number.parseInt(e.target.value, 10) || 1) }),
                },
              })
            }
            className="w-full rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white"
          />
        )}
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="number"
          min={1}
          disabled={segment.reward.type !== "consumable"}
          value={segment.reward.quantity ?? 1}
          onChange={(e) =>
            onChange({
              ...segment,
              reward: {
                ...segment.reward,
                quantity: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
              },
            })
          }
          className="w-14 rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white disabled:opacity-40"
        />
      </td>
      <td className="py-1.5">
        <input
          type="number"
          min={1}
          value={segment.weight}
          onChange={(e) =>
            onChange({
              ...segment,
              weight: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
            })
          }
          className="w-14 rounded bg-kingdom-dark border border-kingdom-mid px-1 py-1 text-white"
        />
      </td>
    </tr>
  );
}

function AdminActionButton({
  label,
  onClick,
  loading,
  disabled,
}: {
  label: string;
  onClick: () => void;
  loading: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
    >
      {loading ? "Working…" : label}
    </button>
  );
}
